import { useFeatureEnabled } from 'api/appConfig';
import { useCurrentIdP, useCurrentScimIdP, useIdPs } from 'api/idps';
import { AxiosError } from 'axios';
import {
	EXTERNAL_ID,
	GENERIC_IDP_ID,
	IdPCommonInputFieldName,
	Method,
	SCIMMethod
} from 'consts';
import { useAuthContext } from 'contexts/AuthContext';
import { useIdPConfigContext } from 'contexts/IdPConfigContext';
import useToast from 'hooks/useToast';
import { useCallback, useMemo } from 'react';
import {
	useMutation,
	useQuery,
	useQueryClient,
	UseQueryOptions
} from 'react-query';
import { IErrorResponse, IIdP, IResConfigs, S4Feature } from 'types/api';
import { ToastSeverity } from 'types/toast';
import { isFeatureEnabledForConfig } from 'utils/s4FeaturesUtils';
import {
	isEnabledConfig,
	queryOidcToOidcFields,
	querySamlToSamlFields,
	queryToScimIdPId,
	queryToXaaIdPId
} from 'utils/loadConfigs';
import { formatErrorResponseMessage } from 'utils/api';
import {
	getCustomSettingId,
	isAuthenticationOnlyConfig,
	isDefaultSSOSettings
} from 'utils/configUtils';
import { del, get, post } from './api';
import { API_ROUTES } from './apiRoutes';
import QueryKeys from './queryKeys';

interface IConfigurationStatus<T> {
	idp?: IIdP;
	method?: T | null;
	guideStepNum?: number | null;
	ssoConfigured?: boolean;
	ssoMappingConfigured?: boolean;
	ssoLastSuccessTestTime?: number | null;
	scimConfigured?: boolean;
	xaaConfigured?: boolean;
}

export const fixFaultyMappings = (configs: IResConfigs) => {
	// fixes a bug where faulty mapping configurations where inserted via console
	// this should fix the faulty mappings and prevent the failure on this client.
	if (configs.saml.samlGroupsMappings) {
		configs.saml.samlGroupsMappings = configs.saml.samlGroupsMappings.filter(
			(mapping) => mapping.groups?.length
		);
	}

	if (configs.oauth?.providerSettings) {
		Object.values(configs.oauth.providerSettings).forEach((provider) => {
			if (provider.oidcGroupsMappings) {
				provider.oidcGroupsMappings = provider.oidcGroupsMappings.filter(
					(mapping) => mapping.groups?.length
				);
			}
		});
	}
};

const getConfigs = async () => {
	const res = await get<IResConfigs>(API_ROUTES.ssoConfig);

	fixFaultyMappings(res.data);
	return res.data;
};

export const useConfigs = <T = IResConfigs>(
	enabled = true,
	options?: UseQueryOptions<IResConfigs, unknown, T, any>
) => {
	const { authRes } = useAuthContext();

	return useQuery({
		queryKey: [QueryKeys.configurations],
		queryFn: getConfigs,
		staleTime: Infinity,
		enabled: enabled && authRes.verified,
		...options
	});
};

export const useSSOConfigurationStatus = () => {
	const { data: idpsData } = useIdPs();
	const { data: configsData, ...configsQueryInfo } = useConfigs();

	const ssoConfigStatus: IConfigurationStatus<Method> | null = useMemo(() => {
		/* istanbul ignore next: no need to test this */
		if (!configsData || !idpsData) {
			return null;
		}

		let idpId = null;
		let configFields = null;
		let method = null;
		let lastSuccessTestTime = null;

		if (isEnabledConfig(configsData, Method.Oidc)) {
			method = Method.Oidc;
			const [configuredIdpId, , configuredMethodFields] =
				queryOidcToOidcFields(configsData);
			idpId = configuredIdpId;
			configFields = configuredMethodFields;

			lastSuccessTestTime =
				configsData.oauth.providerSettings[configsData.tenant.id]
					?.lastSuccessTestTime;
		}

		if (isEnabledConfig(configsData, Method.Saml)) {
			method = Method.Saml;
			const [configuredIdpId, , configuredMethodFields] =
				querySamlToSamlFields(configsData);
			idpId = configuredIdpId;
			configFields = configuredMethodFields;

			lastSuccessTestTime = configsData.saml.lastSuccessTestTime;
		}

		if (!method || !configFields) {
			return {};
		}

		const idp = idpsData.data.find((cIdp) => cIdp.id === idpId);

		return {
			idp,
			method,
			ssoConfigured: true,
			mappingConfigured:
				!!configFields[IdPCommonInputFieldName.UserAttributes]?.length,
			ssoLastSuccessTestTime: lastSuccessTestTime
		};
	}, [configsData, idpsData]);

	return useMemo(
		() => ({
			...configsQueryInfo,
			data: ssoConfigStatus
		}),
		[configsQueryInfo, ssoConfigStatus]
	);
};

export const useSCIMConfigurationStatus = () => {
	const { data: idpsData } = useIdPs();
	const { data: configsData, ...configsQueryInfo } = useConfigs();

	const scimConfigStatus: IConfigurationStatus<SCIMMethod> | null =
		useMemo(() => {
			/* istanbul ignore next: no need to test this */
			if (!configsData || !idpsData) {
				return null;
			}

			let [scimIdPId, scimMethod] = queryToScimIdPId(configsData);
			let scimConfigured = false;
			if (scimMethod === SCIMMethod.Oidc) {
				scimConfigured = !!configsData.oauth.isSCIMKeyGenerated;
			} else if (scimMethod === SCIMMethod.Saml) {
				scimConfigured = !!configsData.saml.isSCIMKeyGenerated;
			} else {
				scimConfigured = !!(
					configsData.saml.isSCIMKeyGenerated ||
					configsData.oauth.isSCIMKeyGenerated
				);
				if (scimConfigured) {
					scimIdPId = GENERIC_IDP_ID;
					scimMethod = SCIMMethod.Generic;
				}
			}
			const scimIdP = idpsData.data.find((idp) => idp.id === scimIdPId);

			return {
				idp: scimIdP,
				method: scimMethod,
				scimConfigured
			};
		}, [configsData, idpsData]);

	return useMemo(
		() => ({
			...configsQueryInfo,
			data: scimConfigStatus
		}),
		[configsQueryInfo, scimConfigStatus]
	);
};

// Cross-App Access (XAA / ID-JAG) is a standalone config (like SCIM): enabled at the
// tenant level with at least one trusted issuer. Derived purely from configsData so the
// home card is independent of SAML/OIDC and of the current navigation context.
export const useXAAConfigurationStatus = () => {
	const { data: idpsData } = useIdPs();
	const { data: configsData, ...configsQueryInfo } = useConfigs();

	const xaaConfigStatus: IConfigurationStatus<Method> | null = useMemo(() => {
		/* istanbul ignore next: no need to test this */
		if (!configsData || !idpsData) {
			return null;
		}

		const issuers = configsData.xaa?.jwtBearerSettings?.issuers ?? {};
		const xaaConfigured =
			!!configsData.xaa?.enabled && Object.keys(issuers).length > 0;

		// Resolve the selected IdP the same way SCIM does so the home card renders the
		// selected-IdP badge. providerID remembers a branded IdP template; it falls back
		// to the generic XAA content id (which has no gallery logo, so no chip shows).
		const [xaaIdPId] = queryToXaaIdPId(configsData);
		const xaaIdP = idpsData.data.find((idp) => idp.id === xaaIdPId);

		return { idp: xaaIdP, xaaConfigured };
	}, [configsData, idpsData]);

	return useMemo(
		() => ({
			...configsQueryInfo,
			data: xaaConfigStatus
		}),
		[configsQueryInfo, xaaConfigStatus]
	);
};

/**
 * returns the current enabled configuration status if exists.
 * if not returns the current idp and method from IdPConfigContext
 */
export const useSSOStatus = () => {
	const { data: ssoConfigsData, ...configsQueryInfo } =
		useSSOConfigurationStatus();

	const { data: currentIdPData } = useCurrentIdP();
	const { idpID, method, guideStepNum } = useIdPConfigContext();

	const ssoStatus: IConfigurationStatus<Method> | null = useMemo(() => {
		if (!ssoConfigsData) {
			return null;
		}

		if (ssoConfigsData.idp) {
			return {
				...ssoConfigsData,
				guideStepNum:
					ssoConfigsData.idp.id === idpID && ssoConfigsData.method === method
						? guideStepNum
						: null
			};
		}

		if (currentIdPData && method) {
			return {
				idp: currentIdPData,
				method,
				guideStepNum
			};
		}

		return {};
	}, [currentIdPData, ssoConfigsData, guideStepNum, idpID, method]);

	return useMemo(
		() => ({
			...configsQueryInfo,
			data: ssoStatus
		}),
		[configsQueryInfo, ssoStatus]
	);
};

/**
 * returns the current enabled configuration status if exists.
 * if not returns the current idp and method from IdPConfigContext
 */
export const useSCIMStatus = () => {
	const { data: scimConfigStatus, ...queryInfo } = useSCIMConfigurationStatus();

	const { data: currentIdPData } = useCurrentScimIdP();
	const { scimIdpID, scimMethod, scimGuideStepNum } = useIdPConfigContext();

	const scimStatus: IConfigurationStatus<SCIMMethod> | null = useMemo(() => {
		if (!scimConfigStatus) {
			return null;
		}

		if (!scimConfigStatus.idp && currentIdPData && scimMethod) {
			return {
				...scimConfigStatus,
				idp: currentIdPData,
				method: scimMethod,
				guideStepNum: scimGuideStepNum
			};
		}

		return {
			...scimConfigStatus,
			guideStepNum:
				scimConfigStatus.idp &&
				scimConfigStatus.idp.id === scimIdpID &&
				scimConfigStatus.method &&
				scimConfigStatus.method === scimMethod
					? scimGuideStepNum
					: null
		};
	}, [
		currentIdPData,
		scimConfigStatus,
		scimGuideStepNum,
		scimIdpID,
		scimMethod
	]);

	return useMemo(
		() => ({
			...queryInfo,
			data: scimStatus
		}),
		[queryInfo, scimStatus]
	);
};

// The suite saves by POSTing back the whole object it loaded, XAA row included - and every SSO
// configuration is created with one. A project that never opted into Cross-App Access must not send
// it: the backend refuses a posted XAA row for such a suite, so the untouched echo would fail an
// ordinary SAML/OIDC save.
//
// configLoaded gates the strip on positive knowledge. The feature reads false while the app config is
// still in flight, and stripping on that would drop the real XAA config of a project that HAS enabled
// it - a silent data loss far worse than the save error this guards against. Unknown means keep.
export const withoutDisabledXaa = (
	configs: IResConfigs,
	xaaEnabled: boolean,
	configLoaded: boolean
): IResConfigs => {
	if (xaaEnabled || !configLoaded || !configs.xaa) {
		return configs;
	}
	const { xaa, ...rest } = configs;
	return rest;
};

export const useUpdateConfigs = (
	successToast: string = intl.tr('ssoConfig.save.saveSuccessMessage'),
	errorToast: string = intl.tr('ssoConfig.save.saveErrorMessage')
) => {
	const queryClient = useQueryClient();
	const { addToast } = useToast();
	const { enabled: xaaEnabled, configLoaded } = useFeatureEnabled(
		S4Feature.Xaa
	);

	return useMutation({
		mutationFn: async (configs: IResConfigs) =>
			post(
				API_ROUTES.ssoConfig,
				withoutDisabledXaa(configs, xaaEnabled, configLoaded)
			),
		onSuccess: (res) => {
			addToast({
				severity: ToastSeverity.Success,
				content: successToast
			});
			queryClient.setQueryData([QueryKeys.configurations], res.data);
		},
		onError: (res: AxiosError<IErrorResponse>) => {
			addToast({
				severity: ToastSeverity.Error,
				content: formatErrorResponseMessage(errorToast, res)
			});
		}
	});
};

export const useUpdateScimConfigs = () => {
	const queryClient = useQueryClient();
	const { addToast } = useToast();

	return useMutation({
		mutationFn: async (configs: IResConfigs) =>
			post(API_ROUTES.scim.all, configs),
		onSuccess: (res) => {
			addToast({
				severity: ToastSeverity.Success,
				content: intl.tr('scimConfig.save.saveSuccessMessage')
			});
			queryClient.setQueryData([QueryKeys.configurations], res.data);
		},
		onError: (res: AxiosError<IErrorResponse>) => {
			addToast({
				severity: ToastSeverity.Error,
				content:
					res.response?.data.errorMessage ||
					intl.tr('scimConfig.save.saveErrorMessage')
			});
		}
	});
};

export interface ISSOTestStartBody {
	redirectUrl: string;
	saml?: IResConfigs['saml'];
	oauth?: IResConfigs['oauth'];
	// Draft SSO domains for the active config, so the backend can enforce the
	// email-domain-mismatch check against domains that are not saved on the tenant yet.
	domains?: string[];
}

export const useStartTestSSO = () =>
	useMutation({
		mutationFn: async (body: ISSOTestStartBody) => {
			const res = await post<
				ISSOTestStartBody,
				{ url: string; errorId?: string }
			>(API_ROUTES.ssoTest.start, body);
			return { url: res.data.url, errorId: res.data.errorId };
		}
	});

export const useDeleteSSOConfigs = () => {
	const queryClient = useQueryClient();
	const { addToast } = useToast();

	return useMutation({
		mutationFn: async () => del(API_ROUTES.ssoConfig),
		onSuccess: (res) => {
			addToast({
				severity: ToastSeverity.Success,
				content: intl.tr('ssoConfig.delete.deleteSuccessMessage')
			});
			queryClient.setQueryData([QueryKeys.configurations], res.data);
		},
		onError: (res: AxiosError<IErrorResponse>) => {
			addToast({
				severity: ToastSeverity.Error,
				content: formatErrorResponseMessage(
					intl.tr('ssoConfig.delete.deleteErrorMessage'),
					res
				)
			});
		}
	});
};

export const useTenantName = () =>
	useConfigs(true, { select: (data) => data.tenant.name });

/** True when this suite session's connection verifies identity only. */
export const useIsAuthenticationOnly = () =>
	useConfigs(true, { select: isAuthenticationOnlyConfig });

/**
 * useFeatureEnabled, minus the features that need a user, when this connection creates none.
 *
 * Not folded into useFeatureEnabled: that one runs during routing and startup, before the SSO
 * configuration this reads has been fetched.
 */
export const useFeatureEnabledForConfig = (feature: S4Feature) => {
	const { enabled, configLoaded } = useFeatureEnabled(feature);
	const { data: authenticationOnly } = useIsAuthenticationOnly();

	return {
		enabled: isFeatureEnabledForConfig(enabled, !!authenticationOnly, feature),
		configLoaded
	};
};

export const useSecondaryConfigName = () =>
	useConfigs(true, {
		select: (data) =>
			data.currentSSOId
				? data.tenant.SSOWrapperMap?.[data.currentSSOId].displayName
				: null
	});

export const useMandatoryUserAttributes = (method: Method | null) => {
	const selectMandatoryAttributes = useCallback(
		(configsData: IResConfigs) => {
			// Cross-App Access (XAA / ID-JAG) enforces NO mandatory user attributes. It has no
			// mandatory list of its own and would otherwise inherit the OIDC config's, but ID-JAG
			// is a machine-to-machine token exchange: the assertion carries whatever the issuing
			// app puts in it, and real IdPs do not send the full profile the SSO mandatory list
			// assumes (email, name, groups, ssoDomains). Requiring those blocks the XAA save on
			// mapping rows the admin has no claim to fill. The Login ID (externalID) row is
			// unaffected - it is required for every method by isMandatoryAttribute, and the runtime
			// falls back to the assertion's `sub` when it is left unmapped.
			if (method === Method.Xaa) {
				return [];
			}

			if (method === Method.Saml) {
				return configsData.saml.mandatoryUserAttributes || [];
			}

			// Oidc
			const mandatoryAttributes =
				configsData.oauth.mandatoryUserAttributes || [];
			if (!mandatoryAttributes.includes(EXTERNAL_ID)) {
				return [EXTERNAL_ID, ...mandatoryAttributes];
			}
			/* istanbul ignore next */
			return mandatoryAttributes;
		},
		[method]
	);

	return useConfigs(true, { select: selectMandatoryAttributes });
};

export const useIsSCIMKeyGenerated = (method: Method | null) => {
	const selectIsSCIMKeyGenerated = useCallback(
		(configsData: IResConfigs) => ({
			isGenerated:
				method && method === Method.Saml
					? configsData.saml.isSCIMKeyGenerated
					: configsData.oauth.isSCIMKeyGenerated
		}),
		[method]
	);

	return useConfigs(true, { select: selectIsSCIMKeyGenerated });
};

export const useJITDisabled = () => {
	const selectJITDisabled = useCallback(
		(configsData: IResConfigs): boolean | null | undefined => {
			/* istanbul ignore next */
			if (!configsData) {
				return null;
			}

			if (isDefaultSSOSettings(configsData)) {
				return configsData.tenant.JITDisabled;
			}

			const customSettingId = getCustomSettingId(configsData);
			/* istanbul ignore next */
			if (!customSettingId) {
				return null;
			}

			return configsData.tenant.JITDisabledSSO?.[customSettingId];
		},
		[]
	);

	return useConfigs(true, { select: selectJITDisabled });
};

export const useMethodSSOId = (method: Method) =>
	useConfigs(true, {
		select: (data) =>
			data.tenant.SSOWrapperMap?.[data.currentSSOId]?.[
				method === Method.Saml ? 'samlId' : 'oauthId'
			] || null
	});
